import type { MediaType } from '@core/attachment/media-type.typings';
import { FileObjectDTO, FileObjectStatusDTO } from '@core/dto/file-object.dto';
import { Model, Serializer } from '@junte/serialize-ts';

export const defaultStatus: FileObjectStatus = 'uploaded';

export type FileObjectStatus = FileObjectStatusDTO | null;

export class FileObjectSerializer implements Serializer<FileObject | null> {
  public deserialize(dto: FileObjectDTO | null): FileObject | null {
    if (!dto) return null;
    return Object.assign<FileObject, Partial<FileObject>>(
      new FileObject([], dto.name, {
        type: dto.contentType,
      }),
      {
        url: dto.url,
        fileSize: dto.size,
        status: dto.status || defaultStatus,
      },
    );
  }

  public serialize(model: FileObject): FileObjectDTO {
    return { ...model, contentType: model.type };
  }
}

@Model(new FileObjectSerializer())
export class FileObject extends File {
  /**
   * Simulate nominal type
   *
   * It is the same as in BaseModel but FileObject already extends File
   * so BaseModel cannot be used as a parent class.
   */
  // @ts-ignore
  private __nominal__: void;

  /**
   * Stores file size retrived from backend
   *
   * File.size is computed based on blob passed on creation time. Blob is
   * not available in the backend response, so there it is this extra property.
   */
  public fileSize: number;

  /**
   * Stores media type
   *
   * It overrides File.type just for better typehint than simple 'string'
   */
  public type: MediaType;
  public status?: FileObjectStatus;
  public url: string;

  public get contentType(): MediaType {
    return this.type;
  }

  public set contentType(value: MediaType) {
    this.type = value;
  }

  public static create(data: Partial<FileObject>): FileObject {
    return Object.assign<FileObject, Partial<FileObject>>(
      new FileObject([], data.name, {
        type: data.type,
      }),
      {
        url: data.url,
        fileSize: data.size,
        status: data.status || defaultStatus,
      },
    );
  }

  public static createFromFile(file: File): FileObject {
    const fo = new FileObject([file], file.name, {
      type: file.type,
    });

    fo.status = null;
    fo.fileSize = file.size;
    fo.url = URL.createObjectURL(file);

    return fo;
  }
}
